/**
 * Retirement Village suggestion engine — generate & rank a curated set of funding/structuring strategies
 * for a chosen village (entry contribution + ongoing fees + exit terms are GIVEN; we optimise how to fund
 * and structure around them). Mirrors the Aged Care / Support at Home goal-leader pattern: each candidate is
 * evaluated with the same RV projection engine the manual path uses, then ranked into Best legacy / Best
 * cashflow / Best Age Pension via the shared `buildRecommendedOptimalStrategies`.
 *
 * A curated BASE set, then composable dimensions — the same shape as the aged care engine. The base
 * strategies are clearly-named, explainable answers to "how do we fund and structure this move" (only
 * those applicable to the client are generated: home present, investment property present, downsizer
 * age eligibility). Orthogonal decisions that combine with any of them — currently debt clearance —
 * are then PAIRED against every base move strategy rather than listed once, so "sell the home, start
 * a super pension AND clear the car loan" is a ranked candidate, not something the adviser has to
 * assemble by hand. The full universe and its exclusions are documented for actuarial review in
 * docs/actuarial-review/C_STRATEGIES.md — change one, change the other.
 */
import { villageInterestHeld, villageInterestToday } from '../../../shared/src/calculation/support/villageExit.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { annuityPreferenceOf, allowsResidual, allowsGuaranteed, annuityCeilingOf, annuityCeilingAmount, type AnnuityPreference } from '../../../shared/src/calculation/annuityPreference.ts';
import { annuitySizesFor } from './annuitySizes.ts';
import { quotedIncomeFor, validQuote, type AnnuityQuote } from './annuityQuote.ts';
import { buildRecommendedOptimalStrategies, type EvaluatedStrategy, type RecommendedStrategyEntry } from './supportAtHomeRecommendedStrategies.ts';
import { candidateReadings } from './planChoiceRule.ts';
import { buildFundingVariants, type FundingMap } from './fundingMechanisms.ts';
import { DOWNSIZER_CONTRIBUTION_CAP } from '../constants/index.ts';
import { calculateAge, formatCurrency } from '../utils/index.ts';
import { FUNERAL_BOND_LIMIT, GIFTING_LIMIT_ANNUAL } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { getRecommendedEmergencyFund } from '../utils/emergencyFund.ts';
// One debt rulebook across modules: the same helper the aged care engine pairs its candidates with
// (it knows reverse mortgages cannot be paid out early, and what each debt is secured against).
import { payableDebtsOf } from '../features/agedCare/DebtPayoffStrategy.tsx';
import { debtsWorthTesting } from './debtsToTest.ts';
import { suggestedPropertyFigures } from '../../../shared/src/calculation/support/investmentPropertyDefaults.ts';
import { EXTRA_ALLOWABLE_AMOUNT } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { purchaseWithinBudget, isStampDutyState } from '../../../shared/src/calculation/stampDuty.ts';
import { allocateDownsizerContribution } from '../../../shared/src/calculation/downsizerContributionUtils.ts';
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import * as calculationApi from '../services/calculationApi.ts';

export interface RetirementVillageInputs {
    entryFee?: number;
    ongoingFees?: number;
    [key: string]: unknown;
}

export interface RvSellHomeDetails {
    salePrice: number;
    sellingCosts: number;
    downsizerContribution: number;
    partnerDownsizerContribution: number;
    commenceIncomeStream: boolean;
    giftingAmount: number;
    funeralBondPurchase: number;
    purchaseAnnuity?: number;
    purchaseInvestmentProperty?: number;
    purchaseShares?: number;
    investmentPropertyRent?: number;
    investmentPropertyExpenses?: number;
    /** The state a bought investment property is in, and the duty on buying it (stampDuty.ts). Absent: no duty modelled. */
    investmentPropertyState?: string;
    investmentPropertyStampDuty?: number;
    rvAnnuityType?: string;
    /** Share of the purchase price promised back on death, 0-100, before the capital access schedule winds it down. Both ends are offered — see above. */
    rvAnnuityResidualPercent?: number;
    /** A provider's quoted annual income, replacing the tool's indicative rate for this candidate. */
    rvAnnuityIncomeOverride?: number;
    [key: string]: unknown;
}

export interface RvRentDetails {
    rentAmount: number;
    rentExpenses: number;
    partnerRent: number;
}

export interface RvInvestmentPropertyDetails {
    selectedPropertyId: string | number | null;
    entryFeeAllocation: number;
    downsizerContribution: number;
    partnerDownsizerContribution: number;
    commenceIncomeStream: boolean;
    giftingAmount: number;
    funeralBondPurchase: number;
}

/** A rounded dollar figure for a strategy NAME — "$1.5m", "$430k" — so a card says how big the
 *  commitment is without a nine-character number in its title. */
function formatRoughAmount(amount: number): string {
    if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(amount >= 10_000_000 ? 0 : 1).replace(/\.0$/, '')}m`;
    if (amount >= 1_000) return `$${Math.round(amount / 1_000)}k`;
    return `$${Math.round(amount)}`;
}

export interface RetirementVillageCandidate {
    id: string;
    name: string;
    description: string;
    fundFrom: 'liquid' | 'sell_home' | 'investment_property';
    /** Debts cleared as part of this strategy — forwarded to the engine's debt payoff plugin. */
    debtPayoff?: { payments?: Record<string, number>; funding?: Record<string, number>; repaymentBasis?: 'keep_repayment' | 'lower_repayment' };
    entryFeeFundingOverride?: FundingMap;
    rentHome?: boolean;
    rentDetails?: RvRentDetails;
    sellHomeDetails?: RvSellHomeDetails;
    investmentPropertyDetails?: RvInvestmentPropertyDetails;
    // evaluateRetirementVillageCandidates (calculationApi.ts) takes Record<string, unknown>[] — this
    // index signature lets a typed RetirementVillageCandidate[] satisfy that without a call-site cast.
    [key: string]: unknown;
}

/**
 * The Strategy Analysis Setup answers (owner, 24 Sep 2026) — the choices the strategies turn on
 * that are the client's to make, asked before the run and saved on the client record (goals.rv*).
 */
/** "Would you consider locking part of the money away for a guaranteed income for life?" */
export type RvAnnuityAppetite = 'none' | 'yes' | 'unsure';
/** "At the end, should the money come back to the family?" — which residual versions to build. */
export type RvAnnuityResidual = 'returned' | 'nil' | 'guaranteed' | 'both' | 'back' | 'all';
const RV_RESIDUAL_ANSWERS: readonly RvAnnuityResidual[] = ['returned', 'nil', 'guaranteed', 'both', 'back', 'all'];

export interface RvEngineSetup {
    /** The state a bought investment property would be in; blank = no duty modelled, said on the card. */
    purchaseState?: string | null;
    /** The most of the freed-up money a lifetime annuity may take, 0–100. Default a quarter. */
    annuityShareCapPct?: number | null;
    /** Whether an annuity may be offered at all. 'none' builds no annuity candidate. Default 'unsure' (both versions). */
    annuityAppetite?: RvAnnuityAppetite | null;
    /** Which residual versions to build when the answer was 'yes'. Default both. */
    annuityResidual?: RvAnnuityResidual | null;
    /**
     * Moving from another village (8 Oct 2026): the current unit's payout is taken as paid at the
     * move — funding the new contribution first — unless the adviser says it comes on the state's
     * timeline and is bridged from savings. Default true.
     */
    oldUnitPaysAtMove?: boolean | null;
    /** The yearly surplus a plan must keep for the cashflow-covered goal. Zero = the bills are paid. */
    cashflowTarget?: number | null;
    /*
     * The four choices on step 3 (village rebuild, owner 20–21 Oct 2026). The home: keep and rent it,
     * sell it, or compare both — with the rent, the yearly costs and the weeks a year it sits empty
     * (two by default). The money kept for emergencies, which every draw keeps back. And which uses of
     * what's left after a sale the plans may try (super pensions, investing, a property, a gift).
     */
    homeRoute?: 'keep' | 'sell' | 'compare' | null;
    rentPerWeek?: number | null;
    rentCosts?: number | null;
    vacancyWeeks?: number | null;
    emergencyReserve?: number | null;
    residualUses?: ReadonlyArray<'super' | 'invest' | 'property' | 'gift'> | null;
}

/** Two weeks a year without a tenant, unless the client says otherwise (village review decision 12, agreed 21 Oct 2026). */
export const DEFAULT_RV_VACANCY_WEEKS = 2;

/** The money kept for emergencies: the setup's answer, else the greater of six months' costs and $50,000. */
export function rvReserveOf(factFind: DraftFactFind, setup: RvEngineSetup | null | undefined): number {
    const raw = Number(setup?.emergencyReserve);
    return setup?.emergencyReserve != null && Number.isFinite(raw) && raw >= 0 ? raw : getRecommendedEmergencyFund(factFind);
}

/** The weekly rent the plans use: the rent less the weeks it sits empty, spread over the year. */
export function rentAfterVacancy(weeklyRent: number, vacancyWeeks: number | null | undefined): number {
    const empty = Math.min(52, Math.max(0, Number.isFinite(Number(vacancyWeeks)) && vacancyWeeks != null ? Number(vacancyWeeks) : DEFAULT_RV_VACANCY_WEEKS));
    return Math.round((weeklyRent * (52 - empty)) / 52 * 100) / 100;
}

/**
 * A quarter, by default. Committing every spare dollar to a lifetime annuity is a position few
 * advisers would take, and "all in" kept winning the cashflow goal on the strength of the income
 * alone; a visible, changeable cap lets the field model the shape a real recommendation has — a
 * guaranteed floor with accessible capital beside it — and the adviser can still say 100.
 */
export const DEFAULT_RV_ANNUITY_SHARE_CAP_PCT = 25;

export function annuityShareCapOf(setup: RvEngineSetup | null | undefined): number {
    const raw = Number(setup?.annuityShareCapPct);
    if (!Number.isFinite(raw) || setup?.annuityShareCapPct == null) return DEFAULT_RV_ANNUITY_SHARE_CAP_PCT;
    return Math.min(100, Math.max(0, raw));
}

/** The saved answers, with the village overview's state as the fallback for the property's. */
export function rvEngineSetupFromGoals(factFind: DraftFactFind | null | undefined, fallbackState?: string | null): RvEngineSetup {
    const goals = (factFind?.goals || {}) as { rvPurchaseState?: string; rvAnnuityShareCapPct?: number; rvCashflowTarget?: number; rvOldUnitPaysAtMove?: boolean; rvState?: string; rvHomeRoute?: 'keep' | 'sell' | 'compare'; rvRentPerWeek?: number; rvRentCosts?: number; rvVacancyWeeks?: number; rvEmergencyReserve?: number; rvResidualUses?: string };
    const saved = typeof goals.rvPurchaseState === 'string' && goals.rvPurchaseState.trim() !== '' ? goals.rvPurchaseState : null;
    // The willingness questions are the CLIENT's, shared with every other module (annuityPreference.ts).
    const preference = annuityPreferenceOf(factFind as never);
    return {
        purchaseState: saved ?? (fallbackState || goals.rvState || ''),
        annuityShareCapPct: goals.rvAnnuityShareCapPct != null && Number.isFinite(Number(goals.rvAnnuityShareCapPct)) ? Number(goals.rvAnnuityShareCapPct) : DEFAULT_RV_ANNUITY_SHARE_CAP_PCT,
        annuityAppetite: preference.appetite,
        annuityResidual: preference.residual,
        // ONE RULE FOR "COVERS THE COSTS" (owner, 23 Oct 2026): money in at least matches money out, as aged care has always used; no module asks for a margin.
        cashflowTarget: 0,
        oldUnitPaysAtMove: typeof goals.rvOldUnitPaysAtMove === 'boolean' ? goals.rvOldUnitPaysAtMove : true,
        homeRoute: goals.rvHomeRoute === 'keep' || goals.rvHomeRoute === 'sell' ? goals.rvHomeRoute : 'compare',
        rentPerWeek: Number.isFinite(Number(goals.rvRentPerWeek)) && goals.rvRentPerWeek != null ? Number(goals.rvRentPerWeek) : null,
        rentCosts: Number.isFinite(Number(goals.rvRentCosts)) && goals.rvRentCosts != null ? Number(goals.rvRentCosts) : null,
        vacancyWeeks: Number.isFinite(Number(goals.rvVacancyWeeks)) && goals.rvVacancyWeeks != null ? Number(goals.rvVacancyWeeks) : DEFAULT_RV_VACANCY_WEEKS,
        emergencyReserve: Number.isFinite(Number(goals.rvEmergencyReserve)) && goals.rvEmergencyReserve != null ? Number(goals.rvEmergencyReserve) : null,
        residualUses: typeof goals.rvResidualUses === 'string'
            ? (goals.rvResidualUses.split(',').map((x) => x.trim()).filter((x) => ['super', 'invest', 'property', 'gift'].includes(x)) as Array<'super' | 'invest' | 'property' | 'gift'>)
            : null,
    };
}

export interface RetirementVillageSuggestionsOptions {
    /** The case village entry date, threaded to the server so every candidate shares the manual run timing. */
    villageEntryDate?: string;
    legacyYears?: number;
    /** The Strategy Analysis Setup answers. Absent: the defaults (no state, a quarter, bills paid). */
    setup?: RvEngineSetup;
    /**
     * A real annuity quote, replacing the tool's indicative rate on every candidate that buys one
     * (owner, 2026-08-26: "it would be good to allow the user to alter the income estimate and have
     * the system remodel on this same scenario with the quote").
     *
     * The engine has always accepted `rvAnnuityIncomeOverride`; this simply lets the adviser reach
     * it from the Suggestion Engine, so a provider's actual figure re-ranks the field instead of
     * sitting beside numbers built on a different income. Applied to the annuity candidates only —
     * nothing else in the field has an annuity income to override.
     *
     * A QUOTE BELONGS TO ONE TYPE AND ONE AMOUNT (guaranteed income review M5, 21 Oct 2026; annuityQuote.ts):
     * plans with the same type use it scaled to their amount; other types keep our estimate.
     */
    annuityQuote?: AnnuityQuote | null;
}

export interface RetirementVillageSuggestionsResult {
    strategies: EvaluatedStrategy[];
    recommended: RecommendedStrategyEntry[];
    /** The stay-home (do nothing) evaluation — the benchmark, rendered as its own baseline card. */
    stayHome: EvaluatedStrategy | null;
}

/**
 * The curated candidate strategies for this client + village (input configs only; evaluation happens below).
 */
export function generateRetirementVillageCandidates(factFind: DraftFactFind, villageInputs: RetirementVillageInputs, setup: RvEngineSetup = {}): RetirementVillageCandidate[] {
    const entryFee = villageInputs?.entryFee || 0;
    /*
     * MOVING FROM ANOTHER VILLAGE (owner, 8 Oct 2026). Taken as paid at the move, the current unit's
     * payout funds the new contribution first: the funding test and the savings allocation below
     * are for the REST. Without this a $1,000,000 entry against $580,000 of savings produced no
     * candidate at all, though $520,000 was coming back from the old unit (owner-caught).
     */
    const payoutAtMove = setup?.oldUnitPaysAtMove !== false && villageInterestHeld(factFind as unknown as FactFind) ? villageInterestToday(factFind as unknown as FactFind) : 0;
    const entryFromLiquid = Math.max(0, entryFee - payoutAtMove);
    /*
     * The plain-words annuity answers: "no" builds no annuity candidate at all; "yes" may narrow
     * the field to one residual version; "not sure" (the default) shows both at the cap.
     *
     * The SETUP's answer wins, and where it is silent the CLIENT RECORD is read — the same answer
     * every other module reads (annuityPreference.ts). Without that fallback a caller who did not
     * thread the setup through would have ignored a client who had said no (caught by
     * annuityPreference.test.ts, 25 Sep 2026).
     */
    const saved = annuityPreferenceOf(factFind as never);
    const annuityAppetite: RvAnnuityAppetite = setup?.annuityAppetite === 'none' || setup?.annuityAppetite === 'yes' || setup?.annuityAppetite === 'unsure'
        ? setup.annuityAppetite
        : saved.appetite;
    const annuityCapPct = annuityAppetite === 'none' ? 0 : annuityShareCapOf(setup);
    const residualAnswer: RvAnnuityResidual = setup?.annuityResidual && RV_RESIDUAL_ANSWERS.includes(setup.annuityResidual)
        ? setup.annuityResidual
        : saved.residual;
    /*
     * INCOME ONLY ONLY ON REQUEST (owner, 21 Oct 2026 — the guaranteed income review D1; annuityPreference.ts).
     * The answers read as one preference: "yes, if the money goes back" (the default, and "not sure") builds
     * money back, and the full amount back where the person may buy it (in or entering subsidised care);
     * "yes, the most income" adds income only. Before this, every client who didn't say no was shown
     * income-only plans.
     */
    const preference: AnnuityPreference = {
        appetite: annuityAppetite,
        residual: annuityAppetite !== 'yes' || residualAnswer === 'both' ? 'back' : residualAnswer,
        answered: true,
    };
    const offerNil = allowsResidual(preference, 0);
    const offerReturned = allowsResidual(preference, 1);
    // The guaranteed shape is sold only to someone in, or entering, government-subsidised care.
    const offerGuaranteed = allowsGuaranteed(preference, factFind as never);
    const purchaseState = isStampDutyState(setup?.purchaseState) ? setup.purchaseState : undefined;
    /** The property a budget buys once its stamp duty is paid, with the rent struck on the PRICE. */
    const propertyFor = (budget: number) => {
        const bought = purchaseWithinBudget(purchaseState, budget, 'investment');
        // The firm's yield, two weeks empty, the agent's 8% and 1% upkeep (investmentPropertyDefaults.ts).
        const f = suggestedPropertyFigures(bought.price);
        return {
            purchaseInvestmentProperty: bought.price,
            investmentPropertyStampDuty: bought.duty,
            investmentPropertyState: purchaseState,
            investmentPropertyRent: f.rent,
            investmentPropertyExpenses: f.expenses,
            investmentPropertyOwner: isCouple ? 'joint' : 'client',
        };
    };
    const dutyWords = (duty: number) => (purchaseState
        ? ` Stamp duty of ${formatCurrency(duty)} (${purchaseState}) comes out of the proceeds, so the property is sized to what is left.`
        : ' Stamp duty is not modelled — no state was chosen for the purchase.');
    const hasHome = (factFind?.home?.value || 0) > 0;
    const homeValue = factFind?.home?.value || 0;
    const investmentProps = factFind?.assets?.investmentProperty || [];
    const hasIp = investmentProps.length > 0;
    const isCouple = factFind?.adviceFor === 'couple';
    const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : null;
    const partnerAge = isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;
    const downsizerEligible = (clientAge != null && clientAge >= 55) || (partnerAge != null && partnerAge >= 55);

    const candidates: RetirementVillageCandidate[] = [];

    // 0. Stay in the current home — the do-nothing baseline every move strategy is measured
    //    against (the same benchmark discipline as the aged care and Support at Home engines).
    //    Evaluated with the village zeroed out: no entry fee, no ongoing fees, no move.
    candidates.push({
        id: 'rv-stay-home',
        name: 'Stay in your current home',
        description: 'No village move — life as it is today. This is the baseline every strategy below is measured against.',
        fundFrom: 'liquid',
        stayHome: true,
    });

    // 1. Fund from savings (keep home as-is). Always available when savings can pay the whole contribution.
    //    EVERY WAY TO DRAW IT (decision 10, 21 Oct 2026): each distinct order of the accounts — and of a
    //    couple's pensions — is its own plan, and the projection decides (fundingMechanisms.buildFundingVariants).
    const reserve = rvReserveOf(factFind, setup);
    const liquidVariants = buildFundingVariants(factFind, entryFromLiquid, reserve);
    const fundedByLiquid = (funding: FundingMap) => Object.values(funding).reduce((sum, amount) => sum + (amount || 0), 0);
    const canFundFromLiquid = liquidVariants.some((v) => fundedByLiquid(v.funding) >= entryFromLiquid - 1);

    if (canFundFromLiquid) {
        liquidVariants.forEach((v) => {
            if (fundedByLiquid(v.funding) < entryFromLiquid - 1) return;
            candidates.push({
                id: `rv-liquid${v.key}`,
                name: `Fund from your savings${v.nameSuffix}`,
                description: `Pay the entry contribution from your savings${v.note ? ` (${v.note.replace(/\.$/, '').replace(/^Drawn/, 'drawn')})` : ''}, and keep your home as it is.`,
                fundFrom: 'liquid',
                entryFeeFundingOverride: v.funding,
            });
        });
    }

    if (hasHome) {
        // 2. Keep and rent the former home (income to cashflow; entry still funded from savings).
        //    Keeping the home means the entry contribution still has to come out of savings, so this
        //    is subject to the same all-or-nothing funding test as the plain savings candidates —
        //    this is the exact strategy that was topping the rankings on a $500k phantom.
        liquidVariants.forEach((v, i) => {
            if (fundedByLiquid(v.funding) < entryFromLiquid - 1) return;
            candidates.push({
                // The usual draw keeps the plain id; each other way of drawing is a plan of its own (decision 10).
                id: i === 0 ? 'rv-liquid-rent' : `rv-liquid-rent${v.key}`,
                name: `Keep and rent your home${i === 0 ? '' : v.nameSuffix}`,
                description: 'Pay the entry contribution from your savings and rent out your former home for extra income.',
                fundFrom: 'liquid',
                // The same draw as the savings plans, so the money kept for emergencies is the client's
                // answer here too, and the card can name the accounts (village rebuild, 21 Oct 2026).
                entryFeeFundingOverride: v.funding,
                rentHome: true,
                rentDetails: {
                    rentAmount: rentAfterVacancy(setup?.rentPerWeek != null ? Number(setup.rentPerWeek) : Number(factFind?.home?.likelyRent) || 0, setup?.vacancyWeeks),
                    rentExpenses: setup?.rentCosts != null ? Number(setup.rentCosts) : Number(factFind?.home?.likelyExpenses) || 0,
                    partnerRent: 0,
                },
            });
        });

        // 3. Sell the home to fund the move.
        const sellingCosts = Math.round(homeValue * 0.025);
        candidates.push({
            id: 'rv-sell-home',
            name: 'Sell your home',
            description: 'Sell the family home, use the proceeds for the entry contribution, and invest the rest.',
            fundFrom: 'sell_home',
            sellHomeDetails: {
                salePrice: homeValue,
                sellingCosts,
                downsizerContribution: 0,
                partnerDownsizerContribution: 0,
                commenceIncomeStream: false,
                giftingAmount: 0,
                funeralBondPurchase: 0,
                purchaseAnnuity: 0,
                purchaseInvestmentProperty: 0,
                purchaseShares: 0,
            },
        });

        const netAfterEntrySell = Math.max(0, homeValue - sellingCosts - entryFee);

        // 4. Sell the home, pay the entry fee, and invest the rest in shares / managed funds.
        if (netAfterEntrySell >= 50000) {
            candidates.push({
                id: 'rv-sell-home-invest',
                // Named for WHAT it buys. "Invest the rest" left an adviser asking "invest in what?",
                // which is the wrong question to be asking of a recommendation.
                name: 'Sell your home and invest in shares / managed funds',
                description: 'Sell the home, pay the entry contribution, and put the freed-up money into a diversified share / managed-fund portfolio instead of the bank — aiming for higher long-term growth and income (still counted for the pension; returns aren\'t guaranteed).',
                fundFrom: 'sell_home',
                sellHomeDetails: {
                    salePrice: homeValue, sellingCosts,
                    downsizerContribution: 0, partnerDownsizerContribution: 0,
                    commenceIncomeStream: false, giftingAmount: 0,
                    funeralBondPurchase: 0, purchaseAnnuity: 0,
                    purchaseInvestmentProperty: 0, purchaseShares: netAfterEntrySell,
                },
            });

            // 5. Sell the home, pay the entry fee, and buy an investment property for rental income.
            //    Yield and expense ratio from the published asset-class assumptions (a licensee's
            //    own figures where their firm has set them) — see investmentProfiles.ts. The stamp
            //    duty on the purchase comes out of the proceeds where a state was chosen (owner,
            //    24 Sep 2026); the property is what is left, and the rent is struck on that.
            const rvIp = propertyFor(netAfterEntrySell);
            candidates.push({
                id: 'rv-sell-home-property',
                name: 'Sell your home and buy an investment property',
                description: 'Sell the home, pay the entry contribution, and use the freed-up money to buy an investment property for rental income and potential growth. The property is an assessable asset and the net rent counts for the pension; it\'s less flexible than shares and has buying/selling costs.' + dutyWords(rvIp.investmentPropertyStampDuty),
                fundFrom: 'sell_home',
                sellHomeDetails: {
                    salePrice: homeValue, sellingCosts,
                    downsizerContribution: 0, partnerDownsizerContribution: 0,
                    commenceIncomeStream: false, giftingAmount: 0,
                    funeralBondPurchase: 0, purchaseAnnuity: 0, purchaseShares: 0,
                    ...rvIp,
                },
            });

            // 6. Sell the home, pay the entry fee, and buy a lifetime annuity for guaranteed income —
            //    sized to the share of the freed-up money the setup allows (a quarter by default,
            //    changeable up to all of it), with the rest invested in shares / managed funds so
            //    it stays accessible. Committing every spare dollar to a lifetime annuity is a
            //    position few advisers would take, and modelled as the only size it kept winning
            //    the cashflow goal on the income alone (owner, 24 Sep 2026). Under $25,000 is too
            //    small to be worth modelling.
            /*
             * HOW MUCH (village review decision 11, 21 Oct 2026; annuitySizing.ts): up to the share the
             * client allows (a quarter by default), and — where it would lift the Age Pension — the amount
             * that lifts it most, as its own plan. Counted assets after the sale are estimated here only to
             * size that plan; the projection decides whether it actually does better.
             */
            /*
             * One rule for every module (annuitySizes.ts, 21 Oct 2026): the client's own ceiling — a share or a
             * dollar amount, asked once — wins over the village's older share answer, and the amount that lifts
             * the Age Pension most is held to it too.
             */
            const annuitySizes = annuityCapPct > 0
                ? annuitySizesFor(factFind, { investable: netAfterEntrySell, capPct: annuityCapPct, homeowner: entryFee > EXTRA_ALLOWABLE_AMOUNT })
                : [];
            for (const size of annuitySizes) {
                const annuityAmount = size.amount;
                const isPensionMax = size.pensionMax;
                const idTail = isPensionMax ? '-max' : '';
                const sharePct = size.sharePct;
                const annuityRest = Math.max(0, netAfterEntrySell - annuityAmount);
                const sizeWords = isPensionMax
                    ? `${formatRoughAmount(annuityAmount)}, the amount that lifts the Age Pension most`
                    : sharePct >= 100
                        ? `all ${formatRoughAmount(annuityAmount)}`
                        : `${formatRoughAmount(annuityAmount)}, ${sharePct}% of what's freed up`;
                const restWords = annuityRest > 0 ? ' and invest the rest' : '';
                // Income only: built only when the client asked for the most income (annuityPreference.ts).
                if (offerNil) candidates.push({
                    id: `rv-sell-home-annuity${idTail}`,
                    name: `Sell your home, buy a lifetime annuity (${sizeWords})${restWords}`,
                    description: `Sell the home, pay the entry contribution, and convert ${sharePct >= 100 ? 'all' : `${sharePct}%`} of the freed-up money into a lifetime annuity — guaranteed income for life, and only 60% of the purchase price counted in the assets test${annuityRest > 0 ? ' — with the rest invested in shares / managed funds where it stays accessible' : ''}. Income only: the highest income, and nothing of the purchase price comes back to the family, even after an early death.`,
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue, sellingCosts,
                        downsizerContribution: 0, partnerDownsizerContribution: 0,
                        commenceIncomeStream: false, giftingAmount: 0,
                        funeralBondPurchase: 0, purchaseInvestmentProperty: 0,
                        purchaseShares: annuityRest,
                        purchaseAnnuity: annuityAmount, rvAnnuityType: 'lifetime',
                        rvAnnuityResidualPercent: 0,
                    },
                });

                /*
                 * THE SAME ANNUITY WITH THE CAPITAL RETURNED — offering only the nil-residual version
                 * disqualified the strategy rather than weighing it.
                 *
                 * Centrelink assesses a lifetime annuity at 60% of its purchase price WHATEVER its
                 * residual capital value: RCV changes the payment rate, not the assets-test treatment.
                 * But net worth values an annuity at its RESIDUAL (assessment/netWorth.ts, deliberately —
                 * a nil-RCV annuity really does leave nothing behind), so the nil version lost the legacy
                 * ranking by its full purchase price on day one and could never win a comparison it was
                 * excluded from. Same fix as the Aged Care engine.
                 */
                if (offerReturned) candidates.push({
                    id: `rv-sell-home-annuity-rcv${idTail}`,
                    name: `Sell your home, buy a lifetime annuity (${sizeWords}, capital returned)${restWords}`,
                    description: `The same annuity, but with the purchase price coming back to the estate on an early death — in full within the first half of life expectancy, less after that — rather than kept by the provider. It pays less income, and Centrelink still counts only 60% either way${annuityRest > 0 ? ' — with the rest invested in shares / managed funds' : ''}.`,
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue, sellingCosts,
                        downsizerContribution: 0, partnerDownsizerContribution: 0,
                        commenceIncomeStream: false, giftingAmount: 0,
                        funeralBondPurchase: 0, purchaseInvestmentProperty: 0,
                        purchaseShares: annuityRest,
                        purchaseAnnuity: annuityAmount, rvAnnuityType: 'lifetime',
                        rvAnnuityResidualPercent: 100,
                    },
                });

                if (offerGuaranteed) candidates.push({
                    id: `rv-sell-home-annuity-guaranteed${idTail}`,
                    name: `Sell your home, buy a lifetime annuity (${sizeWords}, full amount guaranteed)${restWords}`,
                    description: `A lifetime annuity and a life policy bought together, so the full purchase price comes back to the estate whenever death comes. It pays the least income of the three, and only the annuity half earns the 60% assets-test treatment; the policy is counted at what it cost${annuityRest > 0 ? ' — with the rest invested in shares / managed funds' : ''}.`,
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue, sellingCosts,
                        downsizerContribution: 0, partnerDownsizerContribution: 0,
                        commenceIncomeStream: false, giftingAmount: 0,
                        funeralBondPurchase: 0, purchaseInvestmentProperty: 0,
                        purchaseShares: annuityRest,
                        purchaseAnnuity: annuityAmount, rvAnnuityType: 'guaranteed',
                        rvAnnuityResidualPercent: 100,
                    },
                });

            }
        }

        // 7. Sell the home and top up super via the downsizer contribution (if age-eligible).
        if (downsizerEligible) {
            const netAfterEntry = Math.max(0, homeValue - sellingCosts - entryFee);
            // Age-aware: fills the member whose accumulation super is exempt from the means tests
            // (under Age Pension age) FIRST. Where neither or both are exempt this is the same even
            // split it always was — see allocateDownsizerContribution.
            const allocation = allocateDownsizerContribution(factFind as unknown as FactFind, netAfterEntry);
            const downsizer = allocation.client;
            const partnerDownsizer = allocation.partner;
            const preferredName =
                allocation.preferred === 'partner'
                    ? factFind?.partner?.firstName || 'your partner'
                    : allocation.preferred === 'client'
                      ? factFind?.client?.firstName || 'you'
                      : null;
            const preferenceNote = preferredName
                ? ` Weighted to ${preferredName}'s account first: while they are under Age Pension age, super held in their name is not counted in the assets or income tests.`
                : '';
            if (downsizer > 0) {
                candidates.push({
                    id: 'rv-sell-home-downsizer',
                    name: 'Sell your home and top up super',
                    description: `Sell the home, pay the entry contribution, then move freed-up money into super using the downsizer contribution (up to $300,000 each) — often more tax-effective and pension-friendly.${preferenceNote}`,
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue,
                        sellingCosts,
                        downsizerContribution: downsizer,
                        partnerDownsizerContribution: partnerDownsizer,
                        commenceIncomeStream: false,
                        giftingAmount: 0,
                        funeralBondPurchase: 0,
                        purchaseAnnuity: 0,
                        purchaseInvestmentProperty: 0,
                        purchaseShares: 0,
                    },
                });

                // 7a. The same downsizer contribution, then STARTED as an account-based pension
                //     rather than left in accumulation. For a client already over preservation age
                //     this is usually the point of the exercise — earnings become tax free and the
                //     money is drawable — and it is a genuinely different projection, so it is
                //     ranked rather than assumed.
                candidates.push({
                    id: 'rv-sell-home-downsizer-abp',
                    name: 'Sell your home, top up super and start a pension from it',
                    description: 'Sell the home, pay the entry contribution, move freed-up money into super using the downsizer contribution, then commence an account-based pension from it — investment earnings become tax free and the money is drawable as income, rather than sitting in accumulation phase.',
                    fundFrom: 'sell_home',
                    sellHomeDetails: {
                        salePrice: homeValue,
                        sellingCosts,
                        downsizerContribution: downsizer,
                        partnerDownsizerContribution: partnerDownsizer,
                        commenceIncomeStream: true,
                        giftingAmount: 0,
                        funeralBondPurchase: 0,
                        purchaseAnnuity: 0,
                        purchaseInvestmentProperty: 0,
                        purchaseShares: 0,
                    },
                });

                // 7b. Downsizer to the caps commenced as an ABP, and the remainder INVESTED in
                //     shares / managed funds rather than left in cash. Without this candidate the
                //     downsizer family is not comparable with "sell and invest": the plain
                //     downsizer strategies leave everything beyond the caps in a 2%-interest bank
                //     account, so they lose to a full share portfolio on asset allocation alone and
                //     the ranking says nothing about the super wrapper. This is the true
                //     apples-to-apples: the same portfolio, split between the pension wrapper and
                //     personal names — the tax treatment decides.
                const remainderAfterDownsizerAll = Math.max(0, netAfterEntry - downsizer - partnerDownsizer);
                if (remainderAfterDownsizerAll >= 50000) {
                    candidates.push({
                        id: 'rv-sell-home-downsizer-abp-invest',
                        name: 'Sell your home, start a super pension and invest the rest',
                        description: 'Sell the home, pay the entry contribution, move what the downsizer caps allow into super and commence an account-based pension from it, and invest the remainder in shares / managed funds — the same growth portfolio as investing it all personally, but with part of it sheltered inside the pension\'s tax-free wrapper.',
                        fundFrom: 'sell_home',
                        sellHomeDetails: {
                            salePrice: homeValue,
                            sellingCosts,
                            downsizerContribution: downsizer,
                            partnerDownsizerContribution: partnerDownsizer,
                            commenceIncomeStream: true,
                            giftingAmount: 0,
                            funeralBondPurchase: 0,
                            purchaseAnnuity: 0,
                            purchaseInvestmentProperty: 0,
                            purchaseShares: remainderAfterDownsizerAll,
                        },
                    });
                }

                // 7b-ii. The same split, but the remainder buys an INVESTMENT PROPERTY rather than
                //     shares (owner-caught, 2026-08-26: "it never ran a scenario where it sold the
                //     home, put it in super, started a pension and then bought an investment
                //     property with an assumed rental return"). The field already paired the super
                //     pension with a share portfolio, and paired an investment property with a plain
                //     sale — but never the two together, which is a combination advisers genuinely
                //     recommend: the tax-free pension wrapper for what fits the caps, and a rental
                //     income stream for the rest. Rent and expenses use the same assumed yield the
                //     stand-alone investment-property candidate uses, so the two are comparable.
                if (remainderAfterDownsizerAll >= 50000) {
                    candidates.push({
                        id: 'rv-sell-home-downsizer-abp-ip',
                        name: 'Sell your home, start a super pension and buy an investment property',
                        description: 'Sell the home, pay the entry contribution, move what the downsizer caps allow into super and commence an account-based pension from it, then buy an investment property with the remainder — a tax-free pension wrapper alongside rental income. The property is an assessable asset and its net rent counts for the pension, and it is far less flexible than shares if money is needed quickly.',
                        fundFrom: 'sell_home',
                        sellHomeDetails: {
                            salePrice: homeValue,
                            sellingCosts,
                            downsizerContribution: downsizer,
                            partnerDownsizerContribution: partnerDownsizer,
                            commenceIncomeStream: true,
                            giftingAmount: 0,
                            funeralBondPurchase: 0,
                            purchaseAnnuity: 0,
                            ...propertyFor(remainderAfterDownsizerAll),
                            purchaseShares: 0,
                        },
                    });
                }

                // 7c. Downsizer to the caps, and a lifetime annuity with what's left — the two
                //     pension-friendly conversions combined, when meaningful money remains beyond
                //     the super caps. The annuity is sized to the setup's share cap (of the whole
                //     amount freed, as for candidate 6) and the rest invested, so the combination
                //     is comparable with the stand-alone annuity rather than an all-in outlier.
                //     (7d below is the same with the downsizer commenced as an ABP.)
                const remainderAfterDownsizer = Math.max(0, netAfterEntry - downsizer - partnerDownsizer);
                // Held to the client's ceiling (a share or a dollar amount; annuityCeilingOf), of the whole amount freed.
                const downsizerAnnuity = annuityCapPct > 0 ? Math.min(remainderAfterDownsizer, annuityCeilingAmount(annuityCeilingOf(factFind as never, annuityCapPct), netAfterEntry)) : 0;
                /*
                 * WHICH TYPE (guaranteed income review, 21 Oct 2026). This pairing builds one guaranteed income, so it
                 * takes the client's suggested type: money back where allowed; income only only when that is all the
                 * client asked for; the full amount back where it is the only type left and the person may buy it.
                 * (It was income only for every client who didn't ask for money back.)
                 */
                const downsizerAnnuityShape: { rvAnnuityType: 'lifetime' | 'guaranteed'; rvAnnuityResidualPercent: number } | null = offerReturned
                    ? { rvAnnuityType: 'lifetime', rvAnnuityResidualPercent: 100 }
                    : offerNil
                        ? { rvAnnuityType: 'lifetime', rvAnnuityResidualPercent: 0 }
                        : offerGuaranteed ? { rvAnnuityType: 'guaranteed', rvAnnuityResidualPercent: 100 } : null;
                if (downsizerAnnuityShape && remainderAfterDownsizer >= 50000 && downsizerAnnuity >= 25000) {
                    const downsizerAnnuityRest = Math.max(0, remainderAfterDownsizer - downsizerAnnuity);
                    candidates.push({
                        id: 'rv-sell-home-downsizer-annuity',
                        name: 'Sell your home, top up super and buy an annuity',
                        description: `Sell the home, pay the entry contribution, move what the downsizer caps allow into super, and put ${formatRoughAmount(downsizerAnnuity)} of the remainder into a lifetime annuity — combining the super tax shelter with guaranteed income and the annuity's 60% assets-test discount${downsizerAnnuityRest > 0 ? ', the rest invested in shares / managed funds' : ''}.`,
                        fundFrom: 'sell_home',
                        sellHomeDetails: {
                            salePrice: homeValue,
                            sellingCosts,
                            downsizerContribution: downsizer,
                            partnerDownsizerContribution: partnerDownsizer,
                            commenceIncomeStream: false,
                            giftingAmount: 0,
                            funeralBondPurchase: 0,
                            purchaseAnnuity: downsizerAnnuity,
                            ...downsizerAnnuityShape,
                            purchaseInvestmentProperty: 0,
                            purchaseShares: downsizerAnnuityRest,
                        },
                    });

                    // 7d. The same combination with the downsizer money STARTED as an account-based
                    //     pension — a guaranteed income floor from the annuity plus a tax-free,
                    //     drawable pension beside it. This is the shape many real adviser
                    //     recommendations take, so it is ranked rather than left for the adviser to
                    //     assemble by hand.
                    candidates.push({
                        id: 'rv-sell-home-downsizer-abp-annuity',
                        name: 'Sell your home, start a super pension and buy an annuity',
                        description: `Sell the home, pay the entry contribution, move what the downsizer caps allow into super and commence an account-based pension from it, and put ${formatRoughAmount(downsizerAnnuity)} of the remainder into a lifetime annuity — guaranteed income for life with its 60% assets-test discount, plus tax-free investment earnings and drawable income from the pension${downsizerAnnuityRest > 0 ? ', the rest invested in shares / managed funds' : ''}.`,
                        fundFrom: 'sell_home',
                        sellHomeDetails: {
                            salePrice: homeValue,
                            sellingCosts,
                            downsizerContribution: downsizer,
                            partnerDownsizerContribution: partnerDownsizer,
                            commenceIncomeStream: true,
                            giftingAmount: 0,
                            funeralBondPurchase: 0,
                            purchaseAnnuity: downsizerAnnuity,
                            ...downsizerAnnuityShape,
                            purchaseInvestmentProperty: 0,
                            purchaseShares: downsizerAnnuityRest,
                        },
                    });
                }
            }
        }
    }

    // 8. Sell an investment property to fund the move (if one exists).
    if (hasIp) {
        const ip = investmentProps[0];
        candidates.push({
            id: 'rv-sell-ip',
            name: 'Sell an investment property',
            description: 'Sell an investment property and use the net proceeds (after selling costs and CGT) for the entry contribution.',
            fundFrom: 'investment_property',
            investmentPropertyDetails: {
                selectedPropertyId: ip?.id ?? null,
                entryFeeAllocation: entryFee,
                downsizerContribution: 0,
                partnerDownsizerContribution: 0,
                commenceIncomeStream: false,
                giftingAmount: 0,
                funeralBondPurchase: 0,
            },
        });
    }

    /*
     * 8b. THE TWO MEANS-TEST LEVERS THE ENGINE HARD-CODED TO ZERO.
     *
     * Every candidate above sets `giftingAmount: 0` and `funeralBondPurchase: 0`. Both are
     * first-class Manual Control inputs and both act on the problem this module actually creates: a
     * village entry above the extra allowable amount makes the resident a HOMEOWNER, and whatever is
     * left of the sale proceeds is counted in full. A funeral bond takes part of that back outside
     * the assets test; a gift within the free area takes it out altogether.
     *
     * Offered only on the sell-home paths, because they are the ones that produce a residual to
     * allocate. A liquid-funded entry has already spent the savings on the contribution.
     *
     * Gifting stays INSIDE the yearly free area. Above it the excess is still counted, and still
     * deemed to earn income, for five years under the deprivation rules — which this module now
     * models correctly, and which is a real cost the engine cannot weigh against a family's own
     * reasons for a larger gift.
     */
    if (hasHome && entryFee > 0) {
        // The same 2.5% agent/legal estimate the sell-home candidates above use.
        const sellingCosts = Math.round(homeValue * 0.025);
        const residualAfterEntry = Math.max(0, homeValue - sellingCosts - entryFee);
        const isCouple = factFind?.adviceFor === 'couple';
        const baseSellDetails = {
            salePrice: homeValue, sellingCosts,
            downsizerContribution: 0, partnerDownsizerContribution: 0,
            commenceIncomeStream: false, giftingAmount: 0,
            funeralBondPurchase: 0, purchaseAnnuity: 0,
            purchaseInvestmentProperty: 0, purchaseShares: 0,
        };

        const bondAmount = Math.min(residualAfterEntry, FUNERAL_BOND_LIMIT * (isCouple ? 2 : 1));
        if (bondAmount > 0) {
            candidates.push({
                id: 'rv-sell-home-funeral-bond',
                name: 'Sell your home and shelter part of the rest in a funeral bond',
                description: `Sell the home, pay the entry contribution, and put ${formatCurrency(bondAmount)} of what is left into a funeral bond${isCouple ? ' each' : ''} — exempt from the assets test up to ${formatCurrency(FUNERAL_BOND_LIMIT)} per person, so that part stops being counted. The remainder is invested.`,
                fundFrom: 'sell_home',
                sellHomeDetails: {
                    ...baseSellDetails,
                    funeralBondPurchase: bondAmount,
                    purchaseShares: Math.max(0, residualAfterEntry - bondAmount),
                },
            });
        }

        const giftAmount = Math.min(residualAfterEntry, GIFTING_LIMIT_ANNUAL);
        if (giftAmount > 0) {
            candidates.push({
                id: 'rv-sell-home-gift',
                name: 'Sell your home and gift within the free area',
                description: `Sell the home, pay the entry contribution, and gift ${formatCurrency(giftAmount)} of what is left to family. Gifts up to ${formatCurrency(GIFTING_LIMIT_ANNUAL)} a year leave the means test straight away; anything above that is still counted, and still deemed, for five years — so this stays inside the free area. The remainder is invested.`,
                fundFrom: 'sell_home',
                sellHomeDetails: {
                    ...baseSellDetails,
                    giftingAmount: giftAmount,
                    purchaseShares: Math.max(0, residualAfterEntry - giftAmount),
                },
            });
        }
    }

    // 9. Debt clearance — an orthogonal decision PAIRED against every move strategy above, the same
    //    pattern the aged care engine uses. Interest stops, the repayments leave the cashflow and
    //    assessable assets fall — but the money spent is money no other part of the plan can use,
    //    which is exactly the competition worth ranking rather than deciding by instinct. The engine
    //    draws the payoff from liquid assets in its own order, after the entry fee's own draw (the
    //    shared pool means a dollar cannot fund both). The stay-home baseline is left pure: it must
    //    stay indistinguishable from the client's life as it is, or nothing has a benchmark.
    //    (Aged care pairs only a spread of its RAD levels because its base set runs to hundreds;
    //    this base set is a dozen, so every move strategy gets its twin.)
    // Which debts (strategy/debtsToTest.ts, 26 Oct 2026): never a card paid off monthly; any debt that
    // costs interest, whatever its size; interest-free debts from $5,000.
    const debts = debtsWorthTesting(payableDebtsOf(factFind));
    const totalDebt = debts.reduce((sum, d) => sum + d.balance, 0);
    if (debts.length > 0) {
        const payments = Object.fromEntries(debts.map((d) => [d.key, d.balance]));
        const twins = candidates
            .filter((c) => c.stayHome !== true)
            .map((c) => ({
                ...c,
                id: `${c.id}-clear-debt`,
                name: `${c.name} + clear debts (${formatRoughAmount(totalDebt)})`,
                description: `${c.description} This version also clears your outstanding debts from liquid assets at the same time — the interest stops and the repayments leave your cashflow, but the money spent is money the rest of the plan cannot use.`,
                debtPayoff: {
                    payments,
                    repaymentBasis: 'keep_repayment' as const,
                },
            }));
        candidates.push(...twins);
    }

    /*
     * FINAL GATE — nothing is offered that cannot pay the whole entry contribution.
     *
     * The liquid paths are already gated where they are generated (per variant, since one draw order
     * can fund where another falls short). This catches the PROPERTY paths, and expresses the rule
     * once for anything added later: a village ingoing contribution is a contractual price with no
     * right to part-pay it, so a plan that raises less than the full amount is not a strategy.
     *
     * Deliberately conservative on the investment-property path: it is measured on the GROSS value,
     * which can only over-state what the sale nets after costs and CGT. That direction is the safe
     * one — a candidate that grosses enough but nets slightly short is no longer able to flatter
     * itself, because the engine now strikes the exit entitlement on what was actually PAID
     * (VillageContract.entryFeePaid), so it simply ranks on its true, worse numbers instead of
     * winning on a phantom.
     */
    let field = candidates;
    if (entryFee > 0) {
        const netSaleProceeds = hasHome ? homeValue - Math.round(homeValue * 0.025) : 0;
        const grossIpValue = hasIp ? investmentProps.reduce((max, p) => Math.max(max, parseFloat(String(p?.value ?? '')) || 0), 0) : 0;
        field = candidates.filter((candidate) => {
            if (candidate.stayHome === true) return true;
            if (candidate.fundFrom === 'sell_home') return netSaleProceeds >= entryFee - 1;
            if (candidate.fundFrom === 'investment_property') return grossIpValue >= entryFee - 1;
            return true;
        });
    }
    return applyRvChoices(field, setup);
}

/** Which use of what's left a sell plan puts the money to, read from its id (a plain sale leaves it in the bank). */
export function rvResidualUseOf(id: string): 'super' | 'invest' | 'property' | 'gift' | null {
    const bare = id.replace(/-clear-debt$/, '');
    if (/downsizer/.test(bare)) return 'super';
    if (/-gift$/.test(bare)) return 'gift';
    if (/-property$|-ip$/.test(bare)) return 'property';
    if (/-invest$/.test(bare)) return 'invest';
    return null;
}

/*
 * THE CLIENT'S CHOICES, APPLIED (step 3, owner 20–21 Oct 2026). "Keep the home" tests only the plans that
 * keep it; "sell" only those that sell; "compare" both. The uses of what's left after a sale are the ones
 * ticked; a plain sale, which leaves it in the bank, is always tested (decision 14: the bank is always
 * there to compare against). And decision 7: when keeping the home can't fund this unit, the sell plans
 * are shown anyway, each flagged as the only way to fund it.
 */
export function applyRvChoices<C extends RetirementVillageCandidate>(field: C[], setup: RvEngineSetup | null | undefined): C[] {
    const route = setup?.homeRoute ?? 'compare';
    const uses = setup?.residualUses;
    const keeps = (c: C) => c.stayHome === true || c.fundFrom === 'liquid';
    const sells = (c: C) => c.fundFrom === 'sell_home';
    let out = field.filter((c) => {
        if (c.stayHome === true) return true;
        if (route === 'keep' && sells(c)) return false;
        if (route === 'sell' && c.fundFrom === 'liquid') return false;
        if (sells(c) && uses) {
            const use = rvResidualUseOf(String(c.id));
            if (use && !uses.includes(use)) return false;
        }
        return true;
    });
    if (route === 'keep' && !out.some((c) => !c.stayHome && keeps(c))) {
        out = [...out, ...field.filter((c) => sells(c) && (!uses || !rvResidualUseOf(String(c.id)) || uses.includes(rvResidualUseOf(String(c.id))!)))
            .map((c) => ({ ...c, onlyWayToFund: true }))];
    }
    return out;
}

/**
 * Evaluate every candidate with the RV projection engine and rank into goal leaders.
 * @param villageInputs - entry fee, ongoing fees, DMF, growth, capital-gain share, residency years…
 */
export async function buildRetirementVillageSuggestions(
    factFind: DraftFactFind,
    villageInputs: RetirementVillageInputs,
    options: RetirementVillageSuggestionsOptions = {},
): Promise<RetirementVillageSuggestionsResult> {
    const legacyYears = options.legacyYears || 5;
    let candidates = generateRetirementVillageCandidates(factFind, villageInputs, options.setup || {});

    // A quoted income replaces the indicative rate BEFORE evaluation, on the plans of the SAME type, scaled to
    // each plan's amount; plans of another type keep our estimate (annuityQuote.ts).
    const quote = validQuote(options.annuityQuote);
    if (quote) {
        candidates = candidates.map((candidate) => {
            const details = candidate.sellHomeDetails;
            if (!details || !((details.purchaseAnnuity ?? 0) > 0)) return candidate;
            const quotedAnnuityIncome = quotedIncomeFor(quote, details.rvAnnuityType, details.rvAnnuityResidualPercent, details.purchaseAnnuity);
            return quotedAnnuityIncome == null ? candidate : { ...candidate, sellHomeDetails: { ...details, rvAnnuityIncomeOverride: quotedAnnuityIncome } };
        });
    }

    // One request evaluates every candidate against the engine server-side (see
    // server/src/rest/calculation/suggestionEngine/retirementVillage.ts) instead of one request per candidate.
    // The village inputs carry the payout choice to the server, where the moved balance sheet is built.
    const inputsForEngine = { ...villageInputs, oldUnitPaysAtMove: options.setup?.oldUnitPaysAtMove !== false };
    const strategies: EvaluatedStrategy[] = await calculationApi.evaluateRetirementVillageCandidates(factFind, inputsForEngine, candidates, legacyYears, options.villageEntryDate);

    // The stay-home evaluation is the do-nothing BASELINE: goal leaders are ranked among the real
    // move strategies, each measured against it — the same benchmark discipline as the other engines.
    const stayHome = strategies.find((s) => s.id === 'rv-stay-home') || null;
    const moveStrategies = strategies.filter((s) => s.id !== 'rv-stay-home');
    const recommended = buildRecommendedOptimalStrategies(moveStrategies, stayHome, { cashflowTarget: Number(options.setup?.cashflowTarget) || 0, readings: candidateReadings });
    return { strategies, recommended, stayHome };
}
